import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Navbar from "../navbar/navbar";
import Datatable from "../../datatable/Datatable";
import AlertConfirmation from "../../common/AlertConfirmation.component";
import { createPooja, deletePooja, getPoojaById, getPoojaMasterList, updatePooja } from "../../services/admin.service";
import { FaEdit, FaTrash } from "react-icons/fa";
import "../admin/adminModule.css";

const PoojaMasterListComponent = () => {

    const [poojaMasterList, setPoojaMasterList] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState(null);
    const loginId = Number(localStorage.getItem('loginId'));

    useEffect(() => {
        fetchPoojaList();
    }, []);

    const fetchPoojaList = async () => {
        try {
            const response = await getPoojaMasterList();
            if (response) {
                setPoojaMasterList(response);
            } else {
                Swal.fire("Error", "Failed to fetch pooja list", "error");
                setPoojaMasterList([]);
            }
        } catch (error) {
            Swal.fire("Error", "Something went wrong while fetching pooja list", "error");
            console.error(error);
        }
    };

    const timeSlots = Array.from({ length: ((22 - 6) * 2) + 1 }, (_, i) => {
        const hour = 6 + Math.floor(i / 2);
        const minute = i % 2 === 0 ? '00' : '30';
        const suffix = hour >= 12 ? 'PM' : 'AM';
        const displayHour = (hour > 12 ? hour - 12 : hour).toString().padStart(2, '0');
        return `${displayHour}:${minute} ${suffix}`;
    });

    const validationSchema = Yup.object().shape({
        poojaName: Yup.string().required("Pooja Name is required"),
        poojaTime: Yup.string().required("Pooja Time is required"),
        poojaAmount: Yup.number().typeError("Must be a number").required("Pooja Amount is required"),
        noOfPersons: Yup.number().typeError("Must be a number").required("No. of Persons is required"),
    });

    const initialValues = {
        poojaName: editData?.poojaName || "",
        poojaTime: editData?.poojaTime || "",
        poojaAmount: editData?.poojaAmount || "",
        noOfPersons: editData?.noOfPersons || "",
    };

    const columns = [
        { name: "SN", selector: (row) => row.sn, sortable: true, align: 'text-center' },
        { name: "Pooja Name", selector: (row) => row.poojaName, sortable: true, align: 'text-start' },
        { name: "Pooja Time", selector: (row) => row.poojaTime, sortable: true, align: 'text-start' },
        { name: "Pooja Amount", selector: (row) => row.poojaAmount, sortable: true, align: 'text-start' },
        { name: "No of Persons", selector: (row) => row.noOfPersons, sortable: true, align: 'text-start' },
        { name: "Action", selector: (row) => row.action, sortable: true, align: 'text-center' },
    ];


    const mappedData = () => {
        return poojaMasterList.map((item, index) => ({
            sn: index + 1,
            poojaName: item.poojaName || '-',
            poojaTime: item.poojaTime || '-',
            poojaAmount: item.poojaAmount || '-',
            noOfPersons: item.noOfPersons || '-',
            action: (
                <>
                    <button
                        className="btn btn-sm btn-warning me-2"
                        onClick={() => { handleEdit(item) }}
                        title="Edit User"
                    >
                        <FaEdit />
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => { handleDelete(item) }}
                        title="Delete User"
                    >
                        <FaTrash />
                    </button>
                </>
            )
        }))
    }

    const handleAddPooja = () => {
        setEditMode(false);
        setEditData(null);
        setOpenModal(true);
    };

    const handleEdit = async (item) => {
        setEditMode(true);
        const result = await getPoojaById(item.poojaId);
        if (!result) {
            Swal.fire("Error", "Failed to fetch pooja details", "error");
            return;
        }
        setEditData(result);
        setOpenModal(true);
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const isEdit = !!editData;
            const payload = {
                poojaName: values.poojaName,
                poojaTime: values.poojaTime,
                poojaAmount: values.poojaAmount,
                noOfPersons: values.noOfPersons,
                isActive: 1,
                ...(isEdit
                    ? {
                        poojaId: editData.poojaId,
                        modifiedBy: loginId,
                        modifiedDate: new Date().toISOString().split("T")[0],
                    }
                    : {
                        createdBy: loginId,
                        createdDate: new Date().toISOString().split("T")[0],
                    })

            };
            if (editData) {
                // EDIT
                const confirm = await AlertConfirmation({ title: "Are you sure to update!", message: '' });
                if (!confirm) return;
                const result = await updatePooja(editData?.poojaId, payload);
                if (result) {
                    Swal.fire("Updated", "Pooja updated successfully", "success");
                    fetchPoojaList();
                } else {
                    Swal.fire("Error", "Failed to update pooja", "error");
                }
            } else {
                // ADD
                const confirm = await AlertConfirmation({ title: "Are you sure to add!", message: '' });
                if (!confirm) return;
                const result = await createPooja(payload);
                if (result) {
                    Swal.fire("Added", "Pooja created successfully", "success");
                    fetchPoojaList();
                } else {
                    Swal.fire("Error", "Failed to create pooja", "error");
                }
            }
            resetForm();
            setOpenModal(false);
            setEditMode(false);
            setEditData(null);
        } catch (error) {
            Swal.fire("Error", "Something went wrong", "error");
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (item) => {
        if (!item.poojaId) {
            Swal.fire("Error", "Failed to Delete Pooja", "error");
            return;
        }
        const confirm = await AlertConfirmation({ title: "Are you sure to delete!", message: '' });
        if (!confirm) return;
        const result = await deletePooja(item.poojaId);
        if (result) {
            Swal.fire("Deleted", "Pooja deleted successfully", "success");
            fetchPoojaList();
        } else {
            Swal.fire("Error", "Failed to delete pooja", "error");
        }
    };

    return (
        <div className="card">
            <Navbar />
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <h3 className="admin-custom-heading">Pooja Master List</h3>
                    </div>
                </div>
            </div>
            <div id="card-body customized-card" className="p-2">
                {<Datatable columns={columns} data={mappedData()} />}
            </div>
            <div className="text-center mb-3">
                <button type="submit" className="add" onClick={handleAddPooja}>
                    Add New
                </button>
            </div>


            {openModal && (
                <div className="modal show d-block cs-modal-bg" tabIndex="-1">
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header bg-warning">
                                <h5 className="modal-title">{editMode ? "Edit Pooja" : "Add New Pooja"}</h5>
                                <button type="button" className="btn-close" onClick={() => setOpenModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <Formik
                                    enableReinitialize
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    <Form>
                                        <div className="row g-3 align-items-center">
                                            <div className="col-md-5">
                                                <label className="form-label">Pooja Name</label>
                                                <Field type="text" name="poojaName" className="form-control" />
                                                <ErrorMessage name="poojaName" component="div" className="text-danger small" />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">Pooja Time</label>
                                                <Field as="select" name="poojaTime" className="form-select">
                                                    <option value="">Select Time</option>
                                                    {timeSlots.map((slot, i) => (
                                                        <option key={i} value={slot}>{slot}</option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="poojaTime" component="div" className="text-danger small" />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">Pooja Amount</label>
                                                <Field type="number" name="poojaAmount" className="form-control" />
                                                <ErrorMessage name="poojaAmount" component="div" className="text-danger small" />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">No. of Persons</label>
                                                <Field type="number" name="noOfPersons" className="form-control" />
                                                <ErrorMessage name="noOfPersons" component="div" className="text-danger small" />
                                            </div>
                                        </div>

                                        <div className="text-center mt-4">
                                            <button type="submit" className="submit me-2">{editMode ? `Update` : `Submit`}</button>
                                            <button type="button" className="back" onClick={() => setOpenModal(false)}>Cancel</button>
                                        </div>
                                    </Form>
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            )}


        </div>
    )
};

export default PoojaMasterListComponent;